import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import TimelineIcon from '@mui/icons-material/Timeline';
import Chart, { useChart } from '../chart';
import { legacyBinsFrom } from '../../services/particleBins';
import { displayTemperature, temperatureUnit } from '../../utils/temperature';

// ---------------------------------------------------------------------------

const MAX_HISTORY = 120; // 4 minutes at 2s polling

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

function pmQuality(value) {
  if (value <= 12) return { label: 'Good', color: 'success' };
  if (value <= 35.4) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

function co2Quality(value) {
  if (value <= 800) return { label: 'Good', color: 'success' };
  if (value <= 1000) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

// First-pass gas bands (tunable). Exported so they can be unit-tested and
// reused. CO / mercaptan are ppm; NOx is a unitless Sensirion index.
export const CO_GOOD_MAX = 9;
export const CO_ELEVATED_MAX = 35;
export const NOX_GOOD_MAX = 20;
export const NOX_ELEVATED_MAX = 150;
// VOC Index is the Sensirion 1..500 scale, centred on 100 ("typical
// conditions"), so the good band has to sit above the baseline rather than
// near zero the way a ppm band does.
export const VOC_INDEX_GOOD_MAX = 150;
export const VOC_INDEX_ELEVATED_MAX = 250;
export const MERC_GOOD_MAX = 1;
export const MERC_ELEVATED_MAX = 10;

export function coQuality(value) {
  if (value < CO_GOOD_MAX) return { label: 'Good', color: 'success' };
  if (value <= CO_ELEVATED_MAX) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

export function noxQuality(value) {
  if (value < NOX_GOOD_MAX) return { label: 'Good', color: 'success' };
  if (value <= NOX_ELEVATED_MAX) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

export function vocIndexQuality(value) {
  if (value < VOC_INDEX_GOOD_MAX) return { label: 'Good', color: 'success' };
  if (value <= VOC_INDEX_ELEVATED_MAX) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

export function mercQuality(value) {
  if (value < MERC_GOOD_MAX) return { label: 'Good', color: 'success' };
  if (value <= MERC_ELEVATED_MAX) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

function formatTime(date) {
  return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ---------------------------------------------------------------------------

function ReadingCell({ label, value, unit = undefined, quality = null, reserveQuality = false }) {
  const display = isNum(value) ? value : '—';
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{display}{unit && isNum(value) && <Typography component="span" variant="body2" color="text.secondary"> {unit}</Typography>}</Typography>
        {/* Reserve the chip's row even before data so the cell doesn't grow
            when the quality chip first appears. */}
        {(quality || reserveQuality) && (
          <Box sx={{ minHeight: 24, mt: 0.25 }}>
            {quality && <Chip label={quality.label} color={quality.color} size="small" />}
          </Box>
        )}
      </Box>
    </Grid>
  );
}

ReadingCell.propTypes = {
  label: PropTypes.string.isRequired,
   
  value: PropTypes.any,
  unit: PropTypes.string,
  quality: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
  reserveQuality: PropTypes.bool,
};

// The particle section can be read two ways. Only the cumulative concentrations
// are measured; the "> X um per 100 cm3" counts are derived from them, which is
// what the firmware used to do before anything else saw the data. The switch
// exists because that presentation is the one people are used to — not because
// the two are equally good, so the derived side says what it is.
const PARTICLE_VIEWS = { conc: 'per cm³', counts: 'per 100cm³' };

function ParticleViewSwitch({ value, onChange }) {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, v) => { if (v) onChange(v); }}
      size="small"
      sx={{ ml: 1.5 }}
    >
      {Object.entries(PARTICLE_VIEWS).map(([key, label]) => (
        <ToggleButton key={key} value={key} sx={{ px: 1, py: 0 }}>
          <Typography variant="caption">{label}</Typography>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

ParticleViewSwitch.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

// Deriving cannot make these finer — the step is the sensor's 0.1 #/cm3 LSB,
// not the code's — so say so where the numbers are, rather than letting a
// reader take a bin pinned at one LSB for a measurement.
function DerivedBinsCaveat() {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
      Derived from the measured concentrations. The sensor resolves 0.1 particles/cm³,
      so these move in steps of 10 — the smaller sizes carry little signal.
    </Typography>
  );
}

// Which particle family this sensor reports. Both sections used to render
// unconditionally, so a SEN66 unit — which has no PTQS1005 and therefore no
// "larger than X" bins at all — displayed a full set of zeroes that read as a
// measurement of clean air rather than as an absent sensor.
function hasLegacyCounts(r) {
  return [r.pc03, r.pc05, r.pc10, r.pc25, r.pc50, r.pc100].some(isNum);
}

function hasConcentrations(r) {
  return [r.nc05, r.nc10, r.nc25, r.nc40, r.nc100].some(isNum);
}

function ReadingsGrid({ readings, particleView, onParticleViewChange, useFahrenheit }) {
  const r = readings || {};
  const derived = legacyBinsFrom(r);
  return (
    <>
      <Typography variant="overline" color="text.secondary">Mass Concentration</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <ReadingCell label="PM1.0" value={r.pm1} unit="µg/m³" />
        <ReadingCell label="PM2.5" value={r.pm25} unit="µg/m³" reserveQuality quality={isNum(r.pm25) ? pmQuality(r.pm25) : null} />
        <ReadingCell label="PM10" value={r.pm10} unit="µg/m³" />
        <ReadingCell label="CO2" value={r.co2} unit="ppm" reserveQuality quality={isNum(r.co2) ? co2Quality(r.co2) : null} />
      </Grid>
      <Typography variant="overline" color="text.secondary">Environment</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <ReadingCell label="Temperature" value={displayTemperature(r.temp, useFahrenheit)} unit={temperatureUnit(useFahrenheit)} />
        <ReadingCell label="Humidity" value={r.humidity} unit="%" />
        <ReadingCell label="VOC" value={r.voc} unit="ppm" />
      </Grid>
      {/* Two different sensors, two different quantities. A PTQS1005 fills the
          "larger than" ladder; a SEN66 fills the cumulative concentrations.
          Each section is rendered only when its sensor is actually reporting,
          so an AirPlus no longer shows six "larger than" cells reading zero. */}
      {hasLegacyCounts(r) && (
        <>
          <Typography variant="overline" color="text.secondary">Particle Counts (per 100cm³)</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <ReadingCell label=">0.3 µm" value={r.pc03} />
            <ReadingCell label=">0.5 µm" value={r.pc05} />
            <ReadingCell label=">1.0 µm" value={r.pc10} />
            <ReadingCell label=">2.5 µm" value={r.pc25} />
            <ReadingCell label=">5.0 µm" value={r.pc50} />
            <ReadingCell label=">10 µm" value={r.pc100} />
          </Grid>
        </>
      )}
      {hasConcentrations(r) && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="overline" color="text.secondary">
              {particleView === 'counts' ? 'Particle Counts (derived, per 100cm³)' : 'Particle Concentration (per cm³)'}
            </Typography>
            <ParticleViewSwitch value={particleView} onChange={onParticleViewChange} />
          </Box>
          {particleView === 'counts' ? (
            <>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <ReadingCell label=">0.5 µm" value={derived.pc05} />
                <ReadingCell label=">1.0 µm" value={derived.pc10} />
                <ReadingCell label=">2.5 µm" value={derived.pc25} />
                <ReadingCell label=">4.0 µm" value={derived.pc40} />
              </Grid>
              <DerivedBinsCaveat />
            </>
          ) : (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <ReadingCell label="≤0.5 µm" value={r.nc05} />
              <ReadingCell label="≤1.0 µm" value={r.nc10} />
              <ReadingCell label="≤2.5 µm" value={r.nc25} />
              <ReadingCell label="≤4.0 µm" value={r.nc40} />
              <ReadingCell label="≤10 µm" value={r.nc100} />
            </Grid>
          )}
        </>
      )}
      {/* Gas channels — only present on VPSIAIRPLUS hardware. Cells render "—"
          when the channel is absent (raw/ppm null), matching the skeleton idiom. */}
      <Typography variant="overline" color="text.secondary">Gas</Typography>
      <Grid container spacing={2}>
        <ReadingCell label="Carbon Monoxide" value={r.coPpm} unit="ppm" reserveQuality quality={isNum(r.coPpm) ? coQuality(r.coPpm) : null} />
        <ReadingCell label="VOC Index" value={r.vocIndex} reserveQuality quality={isNum(r.vocIndex) ? vocIndexQuality(r.vocIndex) : null} />
        <ReadingCell label="NOx" value={r.nox} reserveQuality quality={isNum(r.nox) ? noxQuality(r.nox) : null} />
        <ReadingCell label="Mercaptan" value={r.mercPpm} unit="ppm" reserveQuality quality={isNum(r.mercPpm) ? mercQuality(r.mercPpm) : null} />
      </Grid>
      {isNum(r.vocIndex) && <VocIndexDisclaimer />}
    </>
  );
}

// The VOC Index is not a concentration and readers will assume it is one unless
// told otherwise: 100 is the sensor's own learned "normal" for the space it is
// in, so the number only means something relative to that sensor's recent
// history. Shown next to the reading rather than buried in docs.
function VocIndexDisclaimer() {
  return (
    <Alert severity="info" sx={{ mt: 2 }}>
      <strong>About the VOC Index:</strong> this is a relative index from 1 to 500,
      not a concentration in ppm. 100 is the baseline this sensor has learned for
      its own surroundings, so higher means more volatile organic compounds than
      has been normal here recently, and lower means cleaner. The baseline adapts
      over roughly 12 hours and restarts when the sensor is power-cycled, so
      compare readings over time from one sensor rather than between sensors.
    </Alert>
  );
}

// ---------------------------------------------------------------------------

function ReadingsChart({ history, particleView, onParticleViewChange, useFahrenheit }) {
  const labels = history.map((h) => formatTime(h.time));

  const sharedXaxis = {
    categories: labels,
    labels: {
      show: true,
      rotate: 0,
      hideOverlappingLabels: true,
      style: { fontSize: '10px' },
    },
    tickAmount: 6,
  };

  const sharedChart = {
    animations: { enabled: true, dynamicAnimation: { speed: 500 } },
    toolbar: { show: false },
  };

  // Particles chart — PM1.0 + PM2.5 + PM10 share left axis
  const pmOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: '\u00b5g/m\u00b3' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const pmSeries = [
    { name: 'PM1.0', data: history.map((h) => h.pm1) },
    { name: 'PM2.5', data: history.map((h) => h.pm25) },
    { name: 'PM10', data: history.map((h) => h.pm10) },
  ];

  // Particle counts chart
  const pcOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: 'particles / 100cm\u00b3' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  // Cumulative concentration chart — #/cm3, so it cannot share the count
  // chart's axis even though both describe particle sizes.
  const ncOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: 'particles / cm\u00b3' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const ncSeries = [
    { name: '\u22640.5\u00b5m', data: history.map((h) => h.nc05) },
    { name: '\u22641.0\u00b5m', data: history.map((h) => h.nc10) },
    { name: '\u22642.5\u00b5m', data: history.map((h) => h.nc25) },
    { name: '\u22644.0\u00b5m', data: history.map((h) => h.nc40) },
    { name: '\u226410\u00b5m', data: history.map((h) => h.nc100) },
  ];

  // The same history read the other way. Derived per point, not from a stored
  // second copy, so the two views can never disagree about one sample.
  const derivedHistory = history.map(legacyBinsFrom);
  const derivedOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: 'particles / 100cm\u00b3 (derived)' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const derivedSeries = [
    { name: '>0.5\u00b5m', data: derivedHistory.map((h) => h.pc05) },
    { name: '>1.0\u00b5m', data: derivedHistory.map((h) => h.pc10) },
    { name: '>2.5\u00b5m', data: derivedHistory.map((h) => h.pc25) },
    { name: '>4.0\u00b5m', data: derivedHistory.map((h) => h.pc40) },
  ];

  const pcSeries = [
    { name: '>0.3\u00b5m', data: history.map((h) => h.pc03) },
    { name: '>0.5\u00b5m', data: history.map((h) => h.pc05) },
    { name: '>1.0\u00b5m', data: history.map((h) => h.pc10) },
    { name: '>2.5\u00b5m', data: history.map((h) => h.pc25) },
    { name: '>5.0\u00b5m', data: history.map((h) => h.pc50) },
    { name: '>10\u00b5m', data: history.map((h) => h.pc100) },
  ];

  // CO2 + VOC chart — multi-axis, since CO2 (~hundreds of ppm), VOC ppm
  // (fractions of a ppm) and the VOC Index (1..500) live on very different
  // scales. A given sensor reports either the ppm channel (PTQS1005) or the
  // index (SEN66/AirPlus), never both, so only one VOC series ever has data.
  const co2Options = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: [
      { seriesName: 'CO2', title: { text: 'CO2 (ppm)' }, min: 0 },
      { seriesName: 'VOC', title: { text: 'VOC (ppm)' }, min: 0, opposite: true },
      { seriesName: 'VOC Index', title: { text: 'VOC Index' }, min: 0, opposite: true },
    ],
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
    colors: ['#FF6B6B', '#4ECDC4', '#8E7CC3'],
  });

  const co2Series = [
    { name: 'CO2', data: history.map((h) => h.co2) },
    { name: 'VOC', data: history.map((h) => h.voc) },
    { name: 'VOC Index', data: history.map((h) => h.vocIndex) },
  ];

  // Environment chart — temp + humidity share left axis
  const envOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      // The trace below is converted, so the axis has to name the same unit.
      title: { text: `${temperatureUnit(useFahrenheit)} / %` },
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const envSeries = [
    // History holds what the sensor reported, in Celsius; the conversion is a
    // display step so a preference change repaints the whole trace.
    { name: 'Temperature', data: history.map((h) => displayTemperature(h.temp, useFahrenheit)) },
    { name: 'Humidity', data: history.map((h) => h.humidity) },
  ];

  if (history.length < 2) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, color: 'text.secondary' }}>
        <Typography variant="body2">Waiting for more data points...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Mass Concentration (PM1.0 / PM2.5 / PM10)
      </Typography>
      <Chart type="line" series={pmSeries} options={pmOptions} height={180} />

      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        CO2 &amp; VOC
      </Typography>
      <Chart type="line" series={co2Series} options={co2Options} height={180} />

      {history.some((h) => isNum(h.pc05)) && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Particle Counts by Size
          </Typography>
          <Chart type="line" series={pcSeries} options={pcOptions} height={200} />
        </>
      )}

      {history.some((h) => isNum(h.nc05)) && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {particleView === 'counts' ? 'Particle Counts by Size (derived)' : 'Particle Concentration by Size'}
            </Typography>
            <ParticleViewSwitch value={particleView} onChange={onParticleViewChange} />
          </Box>
          {particleView === 'counts' ? (
            <>
              <Chart type="line" series={derivedSeries} options={derivedOptions} height={200} />
              <DerivedBinsCaveat />
            </>
          ) : (
            <Chart type="line" series={ncSeries} options={ncOptions} height={200} />
          )}
        </>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Environment
      </Typography>
      <Chart type="line" series={envSeries} options={envOptions} height={180} />
    </Box>
  );
}

// ---------------------------------------------------------------------------

export default function SensorReadings({ serialClient, busyOperation, useFahrenheit = false, legacyCounts = false }) {
  const [readings, setReadings] = useState(null);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null); // null | 'needs-update' | 'warming-up' | 'error'
  const [lastUpdate, setLastUpdate] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [view, setView] = useState('grid');
  // Which way to read the particle section. Lifted here so the grid and the
  // chart cannot disagree, and so switching view does not reset it.
  //
  // The account setting decides what the page opens on; the inline switch still
  // overrides it for this session. Following the preference in an effect rather
  // than only as the initial value means an edit on the account page reaches a
  // page that is already mounted, instead of being ignored until a remount.
  const [particleView, setParticleView] = useState(legacyCounts ? 'counts' : 'conc');
  useEffect(() => {
    setParticleView(legacyCounts ? 'counts' : 'conc');
  }, [legacyCounts]);

  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const addToHistory = useCallback((data) => {
    setHistory((prev) => {
      const entry = {
        time: new Date(),
        pm1: data.pm1,
        pm25: data.pm25,
        pm10: data.pm10,
        co2: data.co2,
        temp: data.temp,
        humidity: data.humidity,
        voc: data.voc,
        vocIndex: data.vocIndex,
        // No `|| 0` here: a channel this sensor does not have is absent, and
        // ApexCharts draws a gap for undefined. Coercing to 0 drew six flat
        // zero lines for every VPSIAIRPLUS, which is a claim about the air.
        pc03: data.pc03,
        pc05: data.pc05,
        pc10: data.pc10,
        pc25: data.pc25,
        pc50: data.pc50,
        pc100: data.pc100,
        nc05: data.nc05,
        nc10: data.nc10,
        nc25: data.nc25,
        nc40: data.nc40,
        nc100: data.nc100,
      };
      const next = [...prev, entry];
      return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
    });
  }, []);

  // Poll sensor readings every 2s
  useEffect(() => {
    const fetchReadings = async () => {
      if (busyOperation) return;
      try {
        const data = await serialClient.readings();
        setReadings(data);
        setLastUpdate(new Date());
        setStatus(null);
        addToHistory(data);
      } catch (err) {
        const reason = err.reason || 'error';
        setStatus(reason);
        if (reason === 'needs-update' && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    };

    fetchReadings();
    pollRef.current = setInterval(fetchReadings, 2000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [serialClient, busyOperation, addToHistory]);

  // Tick "seconds ago" counter every 1s
  useEffect(() => {
    tickRef.current = setInterval(() => {
      if (lastUpdate) {
        setSecondsAgo(Math.floor((Date.now() - lastUpdate.getTime()) / 1000));
      }
    }, 1000);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [lastUpdate]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6">Air Quality</Typography>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, v) => { if (v) setView(v); }}
              size="small"
            >
              <ToggleButton value="grid" sx={{ px: 1, py: 0.25 }}>
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="chart" sx={{ px: 1, py: 0.25 }}>
                <TimelineIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {/* Transient status lives in the fixed-height header row so it never
              reflows the readings below it. */}
          {status === 'warming-up' && (
            <Typography variant="caption" color="text.secondary">Warming up&#8230;</Typography>
          )}
          {status === 'error' && (
            <Typography variant="caption" color="warning.main">Couldn&apos;t read &#8212; retrying&#8230;</Typography>
          )}
          {!status && !readings && (
            <Typography variant="caption" color="text.secondary">Connecting&#8230;</Typography>
          )}
          {!status && lastUpdate && (
            <Typography variant="caption" color="text.secondary">
              Updated: {secondsAgo}s ago
              {history.length > 0 && ` \u00b7 ${history.length} samples`}
            </Typography>
          )}
        </Box>

        {status === 'needs-update' ? (
          <Alert severity="info">
            Live readings require a firmware update. Update your sensor to enable them.
          </Alert>
        ) : view === 'grid' ? (
          // Rendered as a skeleton (— placeholders) from the first paint, so live
          // values fill in place without growing the card or pushing content down.
          <ReadingsGrid
            readings={readings}
            particleView={particleView}
            onParticleViewChange={setParticleView}
            useFahrenheit={useFahrenheit}
          />
        ) : (
          <ReadingsChart
            history={history}
            particleView={particleView}
            onParticleViewChange={setParticleView}
            useFahrenheit={useFahrenheit}
          />
        )}
      </CardContent>
    </Card>
  );
}

SensorReadings.propTypes = {
  serialClient: PropTypes.object.isRequired,
  busyOperation: PropTypes.string,
  useFahrenheit: PropTypes.bool,
  legacyCounts: PropTypes.bool,
};
